import api from './api';
import axios from 'axios';

class S3Service {
  /**
   * Get a signed URL for uploading a file to S3
   * @param {string} fileName - Original file name
   * @param {string} fileType - MIME type of the file
   * @param {string} uploadPath - S3 folder path (members, trainers, equipment, etc.)
   * @returns {Promise<{uploadUrl, fileUrl, s3Key}>}
   */
  async getUploadUrl(fileName, fileType, uploadPath = 'misc') {
    const response = await api.post('/s3/upload-url', {
      fileName,
      fileType,
      uploadPath,
    });
    return response.data.data;
  }

  /**
   * Upload a file directly to S3 using a signed URL
   * @param {string} uploadUrl - Signed URL from getUploadUrl
   * @param {File} file - File object to upload
   * @param {Function} onProgress - Progress callback (optional)
   * @returns {Promise<void>}
   */
  async uploadToS3(uploadUrl, file, onProgress = null) {
    const config = {
      headers: {
        'Content-Type': file.type,
      },
    };

    if (onProgress) {
      config.onUploadProgress = (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        onProgress(percentCompleted);
      };
    }

    // Use native axios (not our api instance) to avoid interceptors
    await axios.put(uploadUrl, file, config);
  }

  /**
   * Complete upload flow: get signed URL and upload file
   * @param {File} file - File object to upload
   * @param {string} uploadPath - S3 folder path
   * @param {Function} onProgress - Progress callback (optional)
   * @returns {Promise<{fileUrl, s3Key}>} - Returns the final file URL to save in DB
   */
  async uploadFile(file, uploadPath = 'misc', onProgress = null) {
    // Step 1: Get signed upload URL
    const { uploadUrl, fileUrl, s3Key } = await this.getUploadUrl(
      file.name,
      file.type,
      uploadPath
    );

    // Step 2: Upload directly to S3
    await this.uploadToS3(uploadUrl, file, onProgress);

    // Step 3: Return the file URL to save in database
    return { fileUrl, s3Key };
  }

  /**
   * Get a signed URL for downloading/viewing a file from S3
   * @param {string} fileUrl - S3 file URL stored in database
   * @param {number} expiresIn - Expiration time in seconds (default: 3600)
   * @returns {Promise<{downloadUrl}>}
   */
  async getDownloadUrl(fileUrl, expiresIn = 3600) {
    const response = await api.post('/s3/download-url', {
      fileUrl,
      expiresIn,
    });
    return response.data.data;
  }

  /**
   * Get signed URLs for multiple files
   * @param {string[]} fileUrls - Array of S3 file URLs
   * @param {number} expiresIn - Expiration time in seconds
   * @returns {Promise<Array<{fileUrl, downloadUrl}>>}
   */
  async getBatchDownloadUrls(fileUrls, expiresIn = 3600) {
    const response = await api.post('/s3/batch-download-urls', {
      fileUrls,
      expiresIn,
    });
    return response.data.data.signedUrls;
  }

  /**
   * Utility: Get a temporary download URL and return it
   * Useful for displaying images or downloading files
   * @param {string} fileUrl - S3 file URL
   * @returns {Promise<string>} - Signed download URL
   */
  async getSignedUrl(fileUrl) {
    if (!fileUrl) return null;
    const { downloadUrl } = await this.getDownloadUrl(fileUrl);
    return downloadUrl;
  }
}

export default new S3Service();
